/**
 * /accounting/periods — Period close & lock console.
 *
 * Steve closes a month here. We:
 *   1. Show every period that has activity (open, closed, locked).
 *   2. Preview the retained-earnings roll before close.
 *   3. Require typed CONFIRM + reason to close (irreversible without explicit reopen).
 *   4. Allow reopen — reverses the RE roll and unlocks postings.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock, Unlock, AlertTriangle, ShieldCheck } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import { computeIncomeStatement, formatPeriodLabel } from "@/data/accounting/reports/financialReports";
import { toast } from "sonner";

import { BooksNavRail } from "@/components/accounting/BooksNavRail";
const fmt = (n: number) =>
  `${n < 0 ? "-" : ""}$${Math.abs(Math.round(n)).toLocaleString()}`;

export default function AccountingPeriodClosePage() {
  useSeedGeneralLedger();
  const { accounts, journalEntries, periodLocks, isPeriodClosed, closePeriod, reopenPeriod } =
    useGeneralLedger();

  const periods = useMemo(() => {
    const set = new Set<string>();
    for (const je of journalEntries) if (je.status === "posted") set.add(je.period);
    for (const p of periodLocks) set.add(p.period);
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [journalEntries, periodLocks]);

  const [closeTarget, setCloseTarget] = useState<string | null>(null);
  const [reopenTarget, setReopenTarget] = useState<string | null>(null);
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");

  const preview = closeTarget
    ? computeIncomeStatement(accounts, journalEntries, closeTarget)
    : null;

  const resetDialog = () => { setConfirm(""); setReason(""); };

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6">
        <Link to="/accounting/gl" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to General Ledger
        </Link>
        <h1 data-tour-id="periods-list" className="text-2xl font-semibold tracking-tight">Period Close</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Close a month to freeze postings and roll revenue + expense into Retained Earnings (3900).
        </p>
      </div>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Period</th>
                <th className="text-right px-3 py-2">Entries</th>
                <th className="text-right px-3 py-2">Net income</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Closed by</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {periods.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No periods with posted activity yet.
                  </td>
                </tr>
              )}
              {periods.map(period => {
                const entries = journalEntries.filter(j => j.status === "posted" && j.period === period);
                const pl = computeIncomeStatement(accounts, journalEntries, period);
                const closed = isPeriodClosed(period);
                const lock = periodLocks.find(p => p.period === period);
                return (
                  <tr key={period} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{formatPeriodLabel(period)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{entries.length}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${pl.netIncome < 0 ? "text-destructive" : ""}`}>{fmt(pl.netIncome)}</td>
                    <td className="px-3 py-2">
                      {closed ? (
                        <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-sm bg-muted">
                          <Lock className="h-3 w-3" /> Closed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-sm bg-accent/40">
                          <Unlock className="h-3 w-3" /> Open
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {lock?.closedBy ? `${lock.closedBy} · ${lock.closedAt?.slice(0, 10)}` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {closed ? (
                        <Button size="sm" variant="outline" onClick={() => { resetDialog(); setReopenTarget(period); }}>
                          Reopen
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => { resetDialog(); setCloseTarget(period); }}>
                          Close period
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CockpitSection>

      {/* Close dialog */}
      <Dialog open={!!closeTarget} onOpenChange={o => !o && setCloseTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Close {closeTarget && formatPeriodLabel(closeTarget)}
            </DialogTitle>
            <DialogDescription>
              Postings to this period will be blocked. Revenue + expense balances will roll into Retained Earnings (3900).
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-1">
              <Row label="Revenue" value={fmt(preview.totalRevenue)} />
              <Row label="COGS" value={fmt(preview.totalCogs)} />
              <Row label="Gross profit" value={fmt(preview.grossProfit)} />
              <Row label="Operating expense" value={fmt(preview.totalOpex)} />
              <div className="border-t border-border mt-1 pt-1">
                <Row
                  label="Net income → Retained Earnings"
                  value={fmt(preview.netIncome)}
                  bold
                  tone={preview.netIncome < 0 ? "neg" : undefined}
                />
              </div>
            </div>
          )}

          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs text-muted-foreground">Reason (≥10 chars)</label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Type CONFIRM to close</label>
              <Input value={confirm} onChange={e => setConfirm(e.target.value)} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseTarget(null)}>Cancel</Button>
            <Button
              disabled={confirm !== "CONFIRM" || reason.trim().length < 10}
              onClick={() => {
                try {
                  closePeriod(closeTarget!, "Steve", reason.trim());
                  toast.success(`Closed ${formatPeriodLabel(closeTarget!)}`, { icon: <ShieldCheck className="h-4 w-4" /> });
                  setCloseTarget(null);
                } catch (e) {
                  toast.error((e as Error).message);
                }
              }}
            >
              Close period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen dialog */}
      <Dialog open={!!reopenTarget} onOpenChange={o => !o && setReopenTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Reopen {reopenTarget && formatPeriodLabel(reopenTarget)}
            </DialogTitle>
            <DialogDescription>
              The retained-earnings roll will be reversed and postings to this period will be allowed again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs text-muted-foreground">Reason (≥10 chars)</label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Type CONFIRM to reopen</label>
              <Input value={confirm} onChange={e => setConfirm(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={confirm !== "CONFIRM" || reason.trim().length < 10}
              onClick={() => {
                try {
                  reopenPeriod(reopenTarget!, reason.trim(), "Steve");
                  toast.success(`Reopened ${formatPeriodLabel(reopenTarget!)}`);
                  setReopenTarget(null);
                } catch (e) {
                  toast.error((e as Error).message);
                }
              }}
            >
              Reopen period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CockpitPage>
  );
}

function Row({ label, value, bold, tone }: { label: string; value: string; bold?: boolean; tone?: "neg" }) {
  return (
    <div className="flex justify-between">
      <span className={tone === "neg" ? "text-destructive" : "text-muted-foreground"}>{label}</span>
      <span className={`tabular-nums ${bold ? "font-semibold" : ""} ${tone === "neg" ? "text-destructive" : ""}`}>{value}</span>
    </div>
  );
}
